import React, { useRef, useEffect, useCallback } from 'react'
import PingPong from '../../ping-pong/src/index.js'
import type { GameConfig, GameCallbacks } from '../../ping-pong/src/types/game'
import { AuthError } from '../../auth/authTools'

interface PingPongGameControllerProps {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  error?: AuthError | null;
}

const PingPongGameController: React.FC<PingPongGameControllerProps> = ({ 
  canvas, 
  ctx, 
  error 
}) => {
  const gameRef = useRef<PingPong | null>(null);

  // Game configuration optimized for background display
  const getGameConfig = useCallback((): Partial<GameConfig> => {
    // Use actual canvas dimensions instead of calculating subset
    // This ensures game elements are positioned correctly within the full canvas
    const gameWidth = canvas?.clientWidth || 1400;
    const gameHeight = canvas?.clientHeight || 1200;
    
    // Debug logging
    console.debug('PingPong Config Debug:', {
      canvasClientWidth: canvas?.clientWidth,
      canvasClientHeight: canvas?.clientHeight,
      canvasActualWidth: canvas?.width,
      canvasActualHeight: canvas?.height,
      gameWidth,
      gameHeight,
      devicePixelRatio: window.devicePixelRatio
    });
    
    return {
      canvas: {
        width: gameWidth,
        height: gameHeight
      },
      ai: {
        difficulty: 0.8,
        reactionTime: 120  // Slightly slower for more realistic gameplay
      },
      ball: {
        speed: Math.max(4, gameWidth / 350),      // Scale speed with game size
        size: Math.max(16, gameWidth / 87.5)      // Scale ball size
      },
      paddle: {
        speed: Math.max(7, gameWidth / 200),      // Scale paddle speed
        width: Math.max(24, gameWidth / 58.3),    // Scale paddle width
        height: Math.max(120, gameHeight / 10)    // Scale paddle height
      },
      visual: {
        colors: {
          background: '#001122',       // Dark blue background
          playerPaddle: '#4caf50',     // Green for left paddle (AI)
          aiPaddle: '#f44336',         // Red for right paddle (AI)
          ball: '#ffeb3b',             // Yellow ball
          centerLine: 'rgba(255, 255, 255, 0.3)',  // Subtle center line
          text: '#ffffff'              // White text (not displayed)
        },
        ui: {
          showOverlayText: false,      // No overlay text for background mode
          showCenterLine: true,        // Keep center line for visual appeal
          font: 'Arial',
          fontSize: Math.max(24, gameWidth / 58.3)  // Scale font size (but hidden)
        }
      }
    }
  }, [canvas]);

  // Game callbacks for state management
  const getGameCallbacks = useCallback((): GameCallbacks => ({
    onScore: (_player: string, newScore: { player: number; ai: number }) => {
      // Silent scoring for background mode
      console.debug(`Background game score: ${newScore.player} - ${newScore.ai}`);
    },
    onGameOver: (winner: string) => {
      // Auto-restart on game over for continuous play
      console.debug(`Background game over: ${winner} wins, restarting...`);
      setTimeout(() => {
        if (gameRef.current) {
          gameRef.current.reset();
          gameRef.current.start();
        }
      }, 1000);
    },
    onStateChange: (newState: { gamePhase: string }) => {
      // Monitor state changes for debugging
      console.debug('Background game state:', newState.gamePhase);
    }
  }), []);

  // Initialize game when canvas is ready
  useEffect(() => {
    if (!canvas || !ctx || gameRef.current) return;

    try {
      // Don't resize canvas - it's already sized by AnimatedCanvas
      // Just initialize PingPong game with current canvas dimensions
      const game = new PingPong(canvas, getGameConfig(), getGameCallbacks());
      gameRef.current = game;

      // Set to AI vs AI mode and start automatically
      game.toggleGameMode(); // Switch to AI vs AI
      
      // Start the game after a brief delay
      setTimeout(() => {
        game.start();
      }, 500);

      console.log('PingPong background game initialized');
    } catch (error) {
      console.error('Failed to initialize PingPong game:', error);
    }
  }, [canvas, ctx, getGameConfig, getGameCallbacks]);

  // Handle canvas resize - update game config when dimensions change
  useEffect(() => {
    if (!canvas || !gameRef.current) return;

    const handleResize = () => {
      if (gameRef.current) {
        const newConfig = getGameConfig();
        gameRef.current.updateConfig(newConfig);
        console.debug('PingPong config updated for resize:', newConfig.canvas);
      }
    };

    // Use ResizeObserver to detect canvas size changes
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
    };
  }, [canvas, getGameConfig]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameRef.current) {
        gameRef.current.cleanup();
        gameRef.current = null;
        console.log('PingPong background game cleaned up');
      }
    };
  }, []);

  // Handle auth error states (optional visual adjustments)
  useEffect(() => {
    if (!gameRef.current) return;

    // Optionally adjust game behavior based on auth errors
    if (error?.type === 'authorization') {
      // Slow down game slightly for auth errors
      gameRef.current.updateConfig({
        ball: { speed: 3, size: 16 },
        ai: { difficulty: 0.6, reactionTime: 150 }
      });
    } else if (error?.type === 'network') {
      // Speed up game for network errors (more activity)
      gameRef.current.updateConfig({
        ball: { speed: 5, size: 16 },
        ai: { difficulty: 0.9, reactionTime: 80 }
      });
    } else {
      // Reset to default configuration
      gameRef.current.updateConfig(getGameConfig());
    }
  }, [error?.type, getGameConfig]);

  // This component renders nothing - the game renders directly to canvas
  return null;
};

export default PingPongGameController;